import React, { useRef, useEffect, useCallback, useState, useImperativeHandle, forwardRef } from 'react';

const GRID_SIZE = 24;
const MIN_SCALE = 0.1;
const MAX_SCALE = 4;
const ZOOM_SENSITIVITY = 0.002;

/**
 * DesignCanvas — pannable/zoomable viewport.
 *
 * - Trackpad two-finger scroll → pan
 * - Trackpad pinch (ctrlKey + wheel) → zoom
 * - Mouse wheel → zoom
 * - Primary-drag on background → pan
 * - Grid background via SVG pattern
 * - All transforms written directly to DOM for 60fps (no React re-render per frame)
 */
const DesignCanvas = forwardRef(({ children, initialScale = 0.75, style }, ref) => {
  const containerRef = useRef(null);
  const worldRef = useRef(null);
  // Keep transform in a ref so updates bypass React's render cycle
  const transformRef = useRef({ x: 60, y: 60, scale: initialScale });
  const draggingRef = useRef(false);
  const lastPointerRef = useRef({ x: 0, y: 0 });
  // Force a single React re-render for the initial mount
  const [, forceUpdate] = useState(0);

  const applyTransform = useCallback(() => {
    if (!worldRef.current) return;
    const { x, y, scale } = transformRef.current;
    worldRef.current.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
  }, []);

  // Initial render
  useEffect(() => {
    applyTransform();
    forceUpdate(n => n + 1);
  }, [applyTransform]);

  const clampScale = (s) => Math.max(MIN_SCALE, Math.min(MAX_SCALE, s));

  useImperativeHandle(ref, () => ({
    zoomIn: () => {
      const t = transformRef.current;
      transformRef.current = { ...t, scale: clampScale(t.scale * 1.25) };
      applyTransform();
    },
    zoomOut: () => {
      const t = transformRef.current;
      transformRef.current = { ...t, scale: clampScale(t.scale * 0.8) };
      applyTransform();
    },
    fit: () => {
      transformRef.current = { x: 60, y: 60, scale: initialScale };
      applyTransform();
    },
  }), [applyTransform, initialScale]);

  // Zoom toward a focal point in container coords
  const zoomAt = useCallback((clientX, clientY, delta) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const focalX = clientX - rect.left;
    const focalY = clientY - rect.top;

    const t = transformRef.current;
    const oldScale = t.scale;
    const newScale = clampScale(oldScale * (1 - delta * ZOOM_SENSITIVITY * 40));
    const ratio = newScale / oldScale;

    transformRef.current = {
      x: focalX - ratio * (focalX - t.x),
      y: focalY - ratio * (focalY - t.y),
      scale: newScale,
    };
    applyTransform();
  }, [applyTransform]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onWheel = (e) => {
      e.preventDefault();

      if (e.ctrlKey) {
        // Trackpad pinch or mouse wheel zoom
        zoomAt(e.clientX, e.clientY, e.deltaY);
      } else {
        // Trackpad pan
        const t = transformRef.current;
        transformRef.current = { ...t, x: t.x - e.deltaX, y: t.y - e.deltaY };
        applyTransform();
      }
    };

    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, [zoomAt, applyTransform]);

  const onPointerDown = useCallback((e) => {
    // Only pan on primary button on the canvas background
    if (e.button !== 0) return;
    if (e.target !== containerRef.current && e.target !== worldRef.current) return;
    draggingRef.current = true;
    lastPointerRef.current = { x: e.clientX, y: e.clientY };
    containerRef.current.setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e) => {
    if (!draggingRef.current) return;
    const dx = e.clientX - lastPointerRef.current.x;
    const dy = e.clientY - lastPointerRef.current.y;
    lastPointerRef.current = { x: e.clientX, y: e.clientY };
    const t = transformRef.current;
    transformRef.current = { ...t, x: t.x + dx, y: t.y + dy };
    applyTransform();
  }, [applyTransform]);

  const onPointerUp = useCallback(() => {
    draggingRef.current = false;
  }, []);

  const gridId = `ds-grid-${React.useId()}`;

  return (
    <div
      ref={containerRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{
        width: '100%',
        height: '100%',
        background: '#f0eee9',
        overflow: 'hidden',
        position: 'relative',
        cursor: draggingRef.current ? 'grabbing' : 'default',
        touchAction: 'none',
        ...style,
      }}
    >
      {/* SVG grid */}
      <svg
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
        aria-hidden
      >
        <defs>
          <pattern id={gridId} width={GRID_SIZE} height={GRID_SIZE} patternUnits="userSpaceOnUse">
            <path
              d={`M ${GRID_SIZE} 0 L 0 0 0 ${GRID_SIZE}`}
              fill="none"
              stroke="rgba(0,0,0,0.06)"
              strokeWidth="0.5"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${gridId})`} />
      </svg>

      {/* World — all content lives here */}
      <div
        ref={worldRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          transformOrigin: '0 0',
          willChange: 'transform',
        }}
      >
        <div style={{ padding: 60 }}>
          {children}
        </div>
      </div>
    </div>
  );
});

DesignCanvas.displayName = 'DesignCanvas';
export default DesignCanvas;
